/**
 * useSubscription — Checks user subscription tier and premium feature access.
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/utils/logger';

interface LimitCheckResult {
  can_create: boolean;
  reason?: string;
  current_count?: number;
  limit?: number;
  is_premium?: boolean;
  reset_date?: string;
}

export function useSubscription() {
  const { user } = useAuth();

  const checkGoalLimit = useCallback(async (): Promise<LimitCheckResult> => {
    if (!user) {
      return { can_create: false, reason: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('check_goal_limit', {
      user_id_param: user.id
    });

    if (error) {
      logger.error('Error checking goal limit:', error);
      return { can_create: false, reason: 'Error checking limit' };
    }

    return data as unknown as LimitCheckResult;
  }, [user]);

  const checkPledgeLimit = useCallback(async (): Promise<LimitCheckResult> => {
    if (!user) {
      return { can_create: false, reason: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('check_pledge_limit', {
      user_id_param: user.id
    });

    if (error) {
      logger.error('Error checking pledge limit:', error);
      return { can_create: false, reason: 'Error checking limit' };
    }

    return data as unknown as LimitCheckResult;
  }, [user]);

  const checkConnectionLimit = useCallback(async (): Promise<LimitCheckResult> => {
    if (!user) {
      return { can_create: false, reason: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('check_connection_limit', {
      user_id_param: user.id
    });

    if (error) {
      logger.error('Error checking connection limit:', error);
      return { can_create: false, reason: 'Error checking limit' };
    }

    return data as unknown as LimitCheckResult;
  }, [user]);

  const checkCertificateLimit = useCallback(async (): Promise<LimitCheckResult> => {
    if (!user) {
      return { can_create: false, reason: 'Not authenticated' };
    }

    const { data, error } = await supabase.rpc('check_certificate_limit', {
      user_id_param: user.id
    });

    if (error) {
      logger.error('Error checking certificate limit:', error);
      return { can_create: false, reason: 'Error checking limit' };
    }

    return data as unknown as LimitCheckResult;
  }, [user]);

  const incrementPledgeCount = useCallback(async (): Promise<void> => {
    if (!user) return;

    const { error } = await supabase.rpc('increment_pledge_count', {
      user_id_param: user.id
    });

    if (error) {
      logger.error('Error incrementing pledge count:', error);
    }
  }, [user]);

  const incrementCertificateCount = useCallback(async (): Promise<void> => {
    if (!user) return;

    const { error } = await supabase.rpc('increment_certificate_count', {
      user_id_param: user.id
    });

    if (error) {
      logger.error('Error incrementing certificate count:', error);
    }
  }, [user]);

  return {
    checkGoalLimit,
    checkPledgeLimit,
    checkConnectionLimit,
    checkCertificateLimit,
    incrementPledgeCount,
    incrementCertificateCount
  };
}
